import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import templateApi from '../../api/templateApi';
import adminAlertTemplateApi from '../../api/adminAlertTemplate.api';
import { resolveAssetUrl } from '../../api/adminAlertTemplate.api';
import editorBridge from './editorBridge';
import TemplateEditorCanvas from './TemplateEditorCanvas';
import TemplatePreview from './TemplatePreview';
import TemplateAutocomplete from './editor/TemplateAutocomplete';
import BrightPatternTemplateSelect from './editor/BrightPatternTemplateSelect';
import './alert-templates.css';

export default function AlertTemplateEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const isNew = templateId === 'new';

  const [template, setTemplate] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [useDescription, setUseDescription] = useState(false);
  const [planInclusions, setPlanInclusions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (isNew) {
          const created = await templateApi.createNew('New Alert Template');
          if (!cancelled) {
            navigate(`/alert-templates/${created.id}/edit`, { replace: true });
          }
          return;
        }
        const data = await templateApi.loadById(templateId);
        if (cancelled) return;
        if (!data) {
          setError('Template not found');
          return;
        }
        setTemplate(data);
        setPreviewUrl(resolveAssetUrl(data.previewurl || ''));
        setUseDescription(Boolean(data.description));
        editorBridge.ready(data);

        const plansRes = await adminAlertTemplateApi.getPlanInclusions(templateId);
        if (!cancelled && plansRes?.success) {
          setPlanInclusions(Array.isArray(plansRes.data) ? plansRes.data : []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load template');
      }
    })();
    return () => { cancelled = true; };
  }, [templateId, isNew, navigate]);

  useEffect(() => {
    const unsub = editorBridge.on('save.template', (path) => {
      if (typeof path === 'string') setPreviewUrl(resolveAssetUrl(path));
    });
    return unsub;
  }, []);

  const handleTemplateChange = (updater) => {
    setTemplate((prev) => (typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }));
  };

  const togglePlanInclusion = (planTierId) => {
    setPlanInclusions((prev) => prev.map((row) => (
      row.planTierId === planTierId ? { ...row, included: !row.included } : row
    )));
  };

  const savePlanInclusions = async (templateId) => {
    const payload = planInclusions.map(({ planTierId, included }) => ({ planTierId, included }));
    const res = await adminAlertTemplateApi.syncPlanInclusions(templateId, payload);
    if (res?.success && Array.isArray(res.data)) {
      setPlanInclusions(res.data);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const trustedEnabled = Boolean(
        template.sendToTrustedContactsEnabled ?? template.send_to_trusted_contacts_enabled
      );
      const trustedTemplateId = template.trustedContactTemplateId
        ?? template.trusted_contact_template_id
        ?? null;

      if (trustedEnabled && !trustedTemplateId) {
        setError('Select a recipient template when trusted contact send is enabled.');
        return;
      }

      const brightPatternEnabled = Boolean(
        template.sendToBrightPatternEnabled ?? template.send_to_bright_pattern_enabled
      );
      const brightPatternReplyTemplateId = template.brightPatternReplyTemplateId
        ?? template.bright_pattern_reply_template_id
        ?? null;
      const brightPattern911TemplateId = template.brightPattern911TemplateId
        ?? template.bright_pattern_911_template_id
        ?? null;
      const brightPatternTemplateId = template.brightPatternTemplateId
        ?? template.bright_pattern_template_id
        ?? null;

      if (brightPatternEnabled && !brightPatternTemplateId) {
        setError('Select a Bright Pattern dashboard template when Bright Pattern send is enabled.');
        return;
      }

      if (brightPatternEnabled && !brightPatternReplyTemplateId) {
        setError('Select a coordinator reply confirmation template for the sender when Bright Pattern send is enabled.');
        return;
      }

      let merged = {
        ...template,
        name: template.name,
        description: template.description,
        type: template.type || 'alert',
        sendToTrustedContactsEnabled: trustedEnabled,
        send_to_trusted_contacts_enabled: trustedEnabled,
        trustedContactTemplateId: trustedEnabled ? trustedTemplateId : null,
        trusted_contact_template_id: trustedEnabled ? trustedTemplateId : null,
        sendToBrightPatternEnabled: brightPatternEnabled,
        send_to_bright_pattern_enabled: brightPatternEnabled,
        brightPatternTemplateId: brightPatternEnabled ? brightPatternTemplateId : null,
        bright_pattern_template_id: brightPatternEnabled ? brightPatternTemplateId : null,
        brightPatternReplyTemplateId: brightPatternEnabled ? brightPatternReplyTemplateId : null,
        bright_pattern_reply_template_id: brightPatternEnabled ? brightPatternReplyTemplateId : null,
        brightPattern911TemplateId: brightPatternEnabled ? brightPattern911TemplateId : null,
        bright_pattern_911_template_id: brightPatternEnabled ? brightPattern911TemplateId : null,
        sendPushEnabled: template.sendPushEnabled ?? template.send_push_enabled ?? true,
        send_push_enabled: template.sendPushEnabled ?? template.send_push_enabled ?? true,
        sendSmsEnabled: template.sendSmsEnabled ?? template.send_sms_enabled ?? true,
        send_sms_enabled: template.sendSmsEnabled ?? template.send_sms_enabled ?? true,
        sendEmailEnabled: template.sendEmailEnabled ?? template.send_email_enabled ?? false,
        send_email_enabled: template.sendEmailEnabled ?? template.send_email_enabled ?? false,
        senderGpsUseEnabled: template.senderGpsUseEnabled ?? template.sender_gps_use_enabled ?? false,
        sender_gps_use_enabled: template.senderGpsUseEnabled ?? template.sender_gps_use_enabled ?? false,
      };
      await new Promise((resolve, reject) => {
        editorBridge.save({
          cb: (err, payload) => {
            if (err) reject(err);
            else {
              if (payload) merged = { ...merged, ...payload };
              resolve();
            }
          },
        });
      });
      const saved = await templateApi.save(merged);
      setTemplate(saved);
      setPreviewUrl(resolveAssetUrl(saved.previewurl || previewUrl));
      if (saved.id && planInclusions.length) {
        await savePlanInclusions(saved.id);
      }
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateThumbnail = () => {
    editorBridge.updatePreview();
  };

  const handlePublish = async () => {
    setSaving(true);
    try {
      await handleSave();
      const published = await templateApi.publish(template.id);
      setTemplate(published);
      alert('Template published and ready for mobile sync.');
      navigate('/alert-templates');
    } catch (err) {
      setError(err.message || 'Publish failed');
    } finally {
      setSaving(false);
    }
  };

  const selectedPlanCount = planInclusions.filter((p) => p.included).length;
  const trustedContactSendEnabled = Boolean(
    template?.sendToTrustedContactsEnabled ?? template?.send_to_trusted_contacts_enabled
  );
  const trustedContactTemplateId = template?.trustedContactTemplateId
    ?? template?.trusted_contact_template_id
    ?? '';
  const brightPatternSendEnabled = Boolean(
    template?.sendToBrightPatternEnabled ?? template?.send_to_bright_pattern_enabled
  );
  const brightPatternReplyTemplateId = template?.brightPatternReplyTemplateId
    ?? template?.bright_pattern_reply_template_id
    ?? '';
  const brightPattern911TemplateId = template?.brightPattern911TemplateId
    ?? template?.bright_pattern_911_template_id
    ?? '';
  const brightPatternTemplateId = template?.brightPatternTemplateId
    ?? template?.bright_pattern_template_id
    ?? '';
  const sendPushEnabled = template?.sendPushEnabled ?? template?.send_push_enabled ?? true;
  const sendSmsEnabled = template?.sendSmsEnabled ?? template?.send_sms_enabled ?? true;
  const sendEmailEnabled = template?.sendEmailEnabled ?? template?.send_email_enabled ?? false;
  const senderGpsUseEnabled = template?.senderGpsUseEnabled ?? template?.sender_gps_use_enabled ?? false;

  if (!template && !error) {
    return <div className="at-page"><p>Loading editor…</p></div>;
  }

  return (
    <div className="at-page">
      <PageHeader
        inline
        trail={[
          { label: 'Alert Templates', to: '/alert-templates' },
          { label: template?.name || 'New Template' },
        ]}
        actions={(
          <>
            <Link to="/alert-templates" className="at-btn at-btn-secondary">Cancel</Link>
            <button type="button" className="at-btn at-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button type="button" className="at-btn at-btn-danger" onClick={handlePublish} disabled={saving || !template?.id}>
              Publish
            </button>
          </>
        )}
      />

      {error && <div className="at-error">{error}</div>}

      <div className="at-editor-meta">
        <div className="at-meta-row at-meta-row-inline">
          <div className="at-field at-field-name">
            <label htmlFor="at-template-name">Name this Alert Template</label>
            <input
              id="at-template-name"
              type="text"
              maxLength={60}
              value={template?.name || ''}
              onChange={(e) => handleTemplateChange({ name: e.target.value })}
              placeholder="e.g. Urgent Assist"
            />
          </div>

          <div className="at-field at-field-type">
            <label htmlFor="at-template-type">Type</label>
            <select
              id="at-template-type"
              value={template?.type || 'alert'}
              onChange={(e) => handleTemplateChange({ type: e.target.value })}
            >
              <option value="alert">Alert</option>
              <option value="additional">Additional</option>
              <option value="manual_entry">Manual Entry</option>
              <option value="none">None of this</option>
            </select>
          </div>

          <div className="at-field at-field-desc">
            <label className="at-desc-toggle">
              <input
                type="checkbox"
                checked={useDescription}
                onChange={(e) => {
                  setUseDescription(e.target.checked);
                  if (!e.target.checked) handleTemplateChange({ description: '' });
                }}
              />
              <span className="at-desc-toggle-box" aria-hidden="true">
                {useDescription && <i className="fa-solid fa-check" />}
              </span>
              <span className="at-desc-toggle-text">Include description for emails and text messages</span>
            </label>
          </div>

          <div className="at-field at-field-desc">
            <label className="at-desc-toggle">
              <input
                type="checkbox"
                checked={senderGpsUseEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  handleTemplateChange({
                    senderGpsUseEnabled: enabled,
                    sender_gps_use_enabled: enabled,
                  });
                }}
              />
              <span className="at-desc-toggle-box" aria-hidden="true">
                {senderGpsUseEnabled && <i className="fa-solid fa-check" />}
              </span>
              <span className="at-desc-toggle-text">Sender GPS use</span>
            </label>
          </div>
        </div>

        {useDescription && (
          <div className="at-meta-row at-meta-row-desc">
            <div className="at-field at-field-desc-full">
              <textarea
                className="at-desc-textarea"
                rows={2}
                value={template?.description || ''}
                onChange={(e) => handleTemplateChange({ description: e.target.value })}
                placeholder="Short description shown in email and SMS notifications…"
              />
            </div>
          </div>
        )}
      </div>

      <div className="at-editor-layout">
        <div className="at-editor-main">
          <TemplateEditorCanvas
            template={template}
            onTemplateChange={handleTemplateChange}
          />
        </div>
        <aside className="at-editor-sidebar">
          <TemplatePreview
            previewUrl={previewUrl}
            onUpdate={handleUpdateThumbnail}
            updating={saving}
          />
        </aside>
      </div>

      <div className="at-plan-panel at-plan-panel-bottom">
        <div className="at-plan-panel-header">
          <div className="at-plan-panel-title">
            <span className="at-plan-panel-icon" aria-hidden="true">
              <i className="fa-solid fa-credit-card" />
            </span>
            <div>
              <h4>Include in subscription plans</h4>
              <p>Select which plans show this alert template on the billing page.</p>
            </div>
          </div>
          {planInclusions.length > 0 && (
            <span className="at-plan-count">
              {selectedPlanCount} of {planInclusions.length} selected
            </span>
          )}
        </div>

        {planInclusions.length === 0 ? (
          <div className="at-plan-empty">
            <i className="fa-solid fa-circle-info" />
            <span>No active subscription plans found.</span>
          </div>
        ) : (
          <div className="at-plan-checklist">
            {planInclusions.map((plan) => (
              <label
                key={plan.planTierId}
                className={`at-plan-option ${plan.included ? 'selected' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={!!plan.included}
                  onChange={() => togglePlanInclusion(plan.planTierId)}
                />
                <span className="at-plan-check" aria-hidden="true">
                  {plan.included && <i className="fa-solid fa-check" />}
                </span>
                <span className="at-plan-label">
                  <span className="at-plan-name">{plan.name}</span>
                  <span className="at-plan-tier">{plan.tier}</span>
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="at-delivery-panels-row">
      <div className="at-plan-panel at-trusted-panel">
        <div className="at-plan-panel-header">
          <div className="at-plan-panel-title">
            <span className="at-plan-panel-icon" aria-hidden="true">
              <i className="fa-solid fa-user-shield" />
            </span>
            <div>
              <h4>Trusted Contact Delivery</h4>
              <p>When a user sends this template, choose which template trusted contacts receive in the app.</p>
            </div>
          </div>
        </div>

        <div className="at-trusted-settings">
          <label className="at-desc-toggle at-trusted-toggle">
            <input
              type="checkbox"
              checked={trustedContactSendEnabled}
              onChange={(e) => {
                const enabled = e.target.checked;
                handleTemplateChange({
                  sendToTrustedContactsEnabled: enabled,
                  send_to_trusted_contacts_enabled: enabled,
                  ...(enabled ? {} : {
                    trustedContactTemplateId: null,
                    trusted_contact_template_id: null,
                  }),
                });
              }}
            />
            <span className="at-desc-toggle-box" aria-hidden="true">
              {trustedContactSendEnabled && <i className="fa-solid fa-check" />}
            </span>
            <span className="at-desc-toggle-text">Enable trusted contact send</span>
          </label>

          {trustedContactSendEnabled && (
            <div className="at-field at-trusted-template-field">
              <label htmlFor="at-trusted-template">Recipient template for trusted contacts</label>
              <TemplateAutocomplete
                value={trustedContactTemplateId}
                excludeId={template?.id}
                placeholder="Search recipient template…"
                emptyLabel="No published templates found"
                onChange={(id) => handleTemplateChange({
                  trustedContactTemplateId: id,
                  trusted_contact_template_id: id,
                })}
              />
              <p className="at-trusted-hint">
                Example: sender uses &quot;Trusted Circle Assist&quot;; contacts receive &quot;Trusted Circle Assist Contact&quot;.
              </p>
            </div>
          )}

          {trustedContactSendEnabled && (
            <div className="at-trusted-channel-toggles">
              <label className="at-desc-toggle">
                <input
                  type="checkbox"
                  checked={sendPushEnabled}
                  onChange={(e) => handleTemplateChange({
                    sendPushEnabled: e.target.checked,
                    send_push_enabled: e.target.checked,
                  })}
                />
                <span className="at-desc-toggle-box" aria-hidden="true">
                  {sendPushEnabled && <i className="fa-solid fa-check" />}
                </span>
                <span className="at-desc-toggle-text">Send push notification</span>
              </label>
              <label className="at-desc-toggle">
                <input
                  type="checkbox"
                  checked={sendSmsEnabled}
                  onChange={(e) => handleTemplateChange({
                    sendSmsEnabled: e.target.checked,
                    send_sms_enabled: e.target.checked,
                  })}
                />
                <span className="at-desc-toggle-box" aria-hidden="true">
                  {sendSmsEnabled && <i className="fa-solid fa-check" />}
                </span>
                <span className="at-desc-toggle-text">Send SMS (or fallback)</span>
              </label>
              <label className="at-desc-toggle">
                <input
                  type="checkbox"
                  checked={sendEmailEnabled}
                  onChange={(e) => handleTemplateChange({
                    sendEmailEnabled: e.target.checked,
                    send_email_enabled: e.target.checked,
                  })}
                />
                <span className="at-desc-toggle-box" aria-hidden="true">
                  {sendEmailEnabled && <i className="fa-solid fa-check" />}
                </span>
                <span className="at-desc-toggle-text">Send email when contact has email</span>
              </label>
            </div>
          )}
        </div>
      </div>

      <div className="at-plan-panel at-trusted-panel at-bright-pattern-panel">
        <div className="at-plan-panel-header">
          <div className="at-plan-panel-title">
            <span className="at-plan-panel-icon" aria-hidden="true">
              <i className="fa-solid fa-headset" />
            </span>
            <div>
              <h4>Bright Pattern Contact Center</h4>
              <p>When a user sends this template, notify Bright Pattern. When the coordinator replies and submits in Bright Pattern, the sender receives the confirmation template in the app.</p>
            </div>
          </div>
        </div>

        <div className="at-trusted-settings">
          <label className="at-desc-toggle at-trusted-toggle">
            <input
              type="checkbox"
              checked={brightPatternSendEnabled}
              onChange={(e) => {
                const enabled = e.target.checked;
                handleTemplateChange({
                  sendToBrightPatternEnabled: enabled,
                  send_to_bright_pattern_enabled: enabled,
                  ...(enabled ? {} : {
                    brightPatternTemplateId: null,
                    bright_pattern_template_id: null,
                    brightPatternReplyTemplateId: null,
                    bright_pattern_reply_template_id: null,
                    brightPattern911TemplateId: null,
                    bright_pattern_911_template_id: null,
                  }),
                });
              }}
            />
            <span className="at-desc-toggle-box" aria-hidden="true">
              {brightPatternSendEnabled && <i className="fa-solid fa-check" />}
            </span>
            <span className="at-desc-toggle-text">Send to Bright Pattern when user sends this template</span>
          </label>

          {brightPatternSendEnabled && (
            <div className="at-field at-trusted-template-field">
              <label htmlFor="at-bright-pattern-dashboard-template">Bright Pattern dashboard template</label>
              <BrightPatternTemplateSelect
                value={brightPatternTemplateId}
                placeholder="Search dashboard template…"
                emptyLabel="No active Bright Pattern templates found"
                onChange={(id) => handleTemplateChange({
                  brightPatternTemplateId: id,
                  bright_pattern_template_id: id,
                })}
              />
              <p className="at-trusted-hint">
                Which contact center dashboard opens in Bright Pattern (e.g. Urgent Assist, Roadside Assistance).
              </p>
            </div>
          )}

          {brightPatternSendEnabled && (
            <div className="at-field at-trusted-template-field">
              <label htmlFor="at-bright-pattern-reply-template">Confirmation template for sender (when coordinator replies and submits)</label>
              <TemplateAutocomplete
                value={brightPatternReplyTemplateId}
                excludeId={template?.id}
                placeholder="Search coordinator reply template…"
                emptyLabel="No published templates found"
                onChange={(id) => handleTemplateChange({
                  brightPatternReplyTemplateId: id,
                  bright_pattern_reply_template_id: id,
                })}
              />
              <p className="at-trusted-hint">
                Example: sender sends &quot;Urgent Assist&quot;; the coordinator replies in Bright Pattern and submits; the sender then sees &quot;We Are Calling You Now&quot; in the app.
              </p>
            </div>
          )}

          {brightPatternSendEnabled && (
            <div className="at-field at-trusted-template-field">
              <label htmlFor="at-bright-pattern-911-template">911 template for user (when coordinator clicks 911)</label>
              <TemplateAutocomplete
                value={brightPattern911TemplateId}
                excludeId={template?.id}
                placeholder="Search 911 template…"
                emptyLabel="No published templates found"
                onChange={(id) => handleTemplateChange({
                  brightPattern911TemplateId: id,
                  bright_pattern_911_template_id: id,
                })}
              />
              <p className="at-trusted-hint">
                Example: coordinator clicks &quot;911 / Send to Central Station&quot; on the Urgent Assist dashboard; the user receives &quot;911 Transfer&quot; in the app.
              </p>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
